import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import isEmail from 'validator/es/lib/isEmail';
import { InvoiceModel } from '@frontend/api-invoices';
import { stateOptions } from '@frontend/geography';
import { Trans } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { classNames } from '@frontend/string';
import {
  BannerNotification,
  CheckboxField,
  CustomChangeEvent,
  DateField,
  DropdownField,
  EmailField,
  SsnField,
  FieldChangeEvent,
  Heading,
  MoneyField,
  NakedButton,
  normalizeChangeHandler,
  PhoneField,
  PopoverDialog,
  RadioField,
  Text,
  TextField,
  useForm,
  usePopoverDialog,
  currency,
} from '@frontend/design-system';
import {
  HOUSING_STATUS_OPTIONS,
  ID_TYPE_OPTIONS,
  PHONE_TYPE_OPTIONS,
  SUFFIX_OPTIONS,
} from '../../../constants/form-options';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { useIdTypeState } from '../../../hooks/use-id-type-state';
import { EnrollmentFormFieldNames, EnrollmentFormState } from '../enrollment-multi-step.types';
import { QuickOfferFieldError } from '../utils/quick-offer';

export type CareCreditEnrollmentFormProps = {
  className?: string;
  invoice?: InvoiceModel;
  formState: EnrollmentFormState;
  errorState: { fields: QuickOfferFieldError[]; message: string } | null;
  submitAttempts: number;
  onChangeField: (field: EnrollmentFormFieldNames, value: string) => void;
  onChangeComplete: (isComplete: boolean) => void;
  onClearIdFields: () => void;
};

const styles = {
  form: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
    width: 100%;
  `,
  twoColumnRow: css`
    display: flex;
    gap: var(--fabric-space-gap-md);

    @media (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: column;
    }

    & > * {
      width: 50%;

      @media (max-width: ${breakpoints.xsmall.max}px) {
        width: 100%;
      }
    }
  `,
  popoverColumn: css`
    display: flex;
    gap: var(--fabric-space-gap-md);
    align-items: center;

    & > :first-child {
      flex: 1;
    }

    & > :last-child {
      flex-shrink: 0;
    }
  `,
  agreeBox: css`
    display: flex;
    gap: var(--fabric-space-gap-xs);
    padding: var(--fabric-space-gap-md);
    background: var(--fabric-color-neutral-5);
    border-radius: var(--fabric-radius-sm);
    border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-20);
  `,
  dialog: css`
    min-width: 400px;
    max-width: min(calc(100% - 48px), 75%, 500px);
    padding: var(--fabric-space-gap-lg);
    border-radius: var(--fabric-radius-md);
  `,
  dialogHeading: css`
    margin-bottom: var(--fabric-space-gap-md);
  `,
  errorMessage: css`
    margin-bottom: var(--fabric-space-gap-xs);
  `,
  staticAmountField: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xxs);
  `,
};

export const CareCreditEnrollmentForm = ({
  className,
  invoice,
  formState,
  errorState,
  onChangeField,
  onChangeComplete,
  onClearIdFields,
  submitAttempts,
}: CareCreditEnrollmentFormProps) => {
  const { t } = useCareCreditTranslations();

  const { fields } = formState;

  const formattedBilledAmount = invoice ? currency(invoice.billedAmount / 100, { decimals: 2 }) : t('N/A');

  const { showIdState, showMilitaryBranch, showIdCountry } = useIdTypeState(fields.idType);

  /**
   * Field label map
   */

  const FIELD_LABEL_MAP = useMemo<Record<EnrollmentFormFieldNames, string>>(() => {
    return {
      firstName: t('First Name'),
      middleName: t('Middle Name (Optional)'),
      lastName: t('Last Name'),
      suffix: t('Suffix (Optional)'),
      dateOfBirth: t('Birthdate'),
      email: t('Email (Optional)'),
      ssn: t('SSN'),
      phoneNumber: t('Phone Number'),
      phoneNumberType: t('Phone Number Type'),
      address: t('Mailing Address Line 1'),
      address2: t('Mailing Address Line 2 (Optional)'),
      city: t('City'),
      state: t('Issuing State'),
      postalCode: t('Zip Code'),
      housingStatus: t('Housing Status'),
      monthlyNetIncome: t('Monthly Net Income (From all sources)'),
      idType: t('ID Type'),
      idNumber: t('ID Number'),
      idExpirationDate: t('ID Expiration Date'),
      idState: t('ID Issuing State'),
      idCountry: t('ID Issuing Country'),
      idMilitaryBranch: t('Military Branch'),
      agreeToTerms: t('Terms Agreement'),
    };
  }, []);

  const { getFieldProps, isComplete, validate, seedValues } = useForm({
    fields: {
      firstName: {
        type: 'text',
        required: true,
        value: fields.firstName,
        validator: (field) => {
          if (!field.value.trim()) {
            return t('First name is required');
          }
          if (field.value.length > 40) {
            return t('First name cannot exceed 40 characters');
          }
          return '';
        },
      },
      middleName: {
        type: 'text',
        required: false,
        value: fields.middleName,
        validator: (field) => {
          if (field.value.length > 30) {
            return t('Middle name cannot exceed 30 characters');
          }
          return '';
        },
      },
      lastName: {
        type: 'text',
        required: true,
        value: fields.lastName,
        validator: (field) => {
          if (!field.value.trim()) {
            return t('Last name is required');
          }
          if (field.value.length > 40) {
            return t('Last name cannot exceed 40 characters');
          }
          return '';
        },
      },
      suffix: { type: 'dropdown', required: false, value: fields.suffix },
      dateOfBirth: {
        type: 'date',
        required: true,
        value: fields.dateOfBirth,
        validator: (field) => {
          if (!field.value) {
            return t('Birthdate is required');
          }
          const today = dayjs().startOf('day').toDate();
          const dob = dayjs(field.value).startOf('day').toDate();

          if (dob >= today) {
            return t('Birthdate cannot be in the future');
          }
          return '';
        },
      },
      email: {
        type: 'email',
        required: false,
        value: fields.email,
        validator: (field) => {
          if (field.value && field.value.length > 50) {
            return t('Email cannot exceed 50 characters');
          }
          if (field.value && !isEmail(field.value)) {
            return t('Invalid email address');
          }
          return '';
        },
      },
      ssn: {
        type: 'text',
        required: true,
        value: fields.ssn,
        validator: (field) => {
          const ssnDigits = field.value.replace(/-/g, '');
          if (!/^\d{9}$/.test(ssnDigits)) {
            return t('SSN must be 9 digits');
          }
          return '';
        },
      },
      phoneNumber: {
        type: 'text',
        required: true,
        value: fields.phoneNumber,
        validator: (field) => {
          const phoneDigits = field.value.replace(/[-()\s]/g, '');
          if (!/^\d{10}$/.test(phoneDigits)) {
            return t('Phone number must be 10 digits');
          }
          return '';
        },
      },
      phoneNumberType: { type: 'text', required: true, value: fields.phoneNumberType },
      address: {
        type: 'text',
        required: true,
        value: fields.address,
        validator: (field) => {
          if (!field.value.trim()) {
            return t('Address is required');
          }
          if (field.value.length > 55) {
            return t('Address cannot exceed 55 characters');
          }
          return '';
        },
      },
      address2: {
        type: 'text',
        required: false,
        value: fields.address2,
        validator: (field) => {
          if (field.value.length > 55) {
            return t('Address Line 2 cannot exceed 55 characters');
          }
          return '';
        },
      },
      city: {
        type: 'text',
        required: true,
        value: fields.city,
        validator: (field) => {
          if (!field.value.trim()) {
            return t('City is required');
          }
          if (field.value.length > 45) {
            return t('City cannot exceed 45 characters');
          }
          return '';
        },
      },
      state: { type: 'text', required: true, value: fields.state },
      postalCode: {
        type: 'text',
        required: true,
        value: fields.postalCode,
        validator: (field) => {
          // must be between 4 and 9 digits
          const zipDigits = field.value.replace(/[-\s]/g, '');
          if (!/^\d{4,9}$/.test(zipDigits)) {
            return t('Zip code must be between 4 and 9 digits');
          }
          return '';
        },
      },
      housingStatus: { type: 'radio', required: true, value: fields.housingStatus },
      monthlyNetIncome: { type: 'money', required: true, value: fields.monthlyNetIncome },
      idType: { type: 'dropdown', required: true, value: fields.idType },
      idNumber: {
        type: 'text',
        required: true,
        value: fields.idNumber,
        validator: (field) => {
          if (!field.value.trim()) {
            return t('ID number is required');
          }
          if (field.value.length > 30) {
            return t('ID number cannot exceed 30 characters');
          }
          return '';
        },
      },
      idExpirationDate: {
        type: 'date',
        required: true,
        value: fields.idExpirationDate,
        validator: (field) => {
          if (!field.value) {
            return t('ID expiration date is required');
          }

          if (dayjs(field.value).isBefore(dayjs().add(1, 'day'), 'day')) {
            return t('ID expiration date must be in the future');
          }
          return '';
        },
      },
      idState: { type: 'dropdown', required: showIdState, value: fields.idState },
      idCountry: {
        type: 'text',
        required: showIdCountry,
        value: fields.idCountry,
        validator: (field) => {
          // 2-3 chars
          if (showIdCountry && !/^[A-Za-z]{2,3}$/.test(field.value)) {
            return t('ID issuing country must be 2 or 3 letter country code');
          }
          return '';
        },
      },
      idMilitaryBranch: { type: 'text', required: showMilitaryBranch, value: fields.idMilitaryBranch },
      agreeToTerms: { type: 'checkbox', required: true, value: fields.agreeToTerms },
    },
  });

  const onAnyFieldChange = (e?: FieldChangeEvent) => {
    if (e) {
      normalizeChangeHandler(({ name, value }) => {
        const fieldProps = getFieldProps(name as EnrollmentFormFieldNames);
        onChangeField(name as EnrollmentFormFieldNames, value);
        fieldProps.onChange(e);
      })(e as CustomChangeEvent);
    }
  };

  const onChangeIdType = (e?: FieldChangeEvent) => {
    onAnyFieldChange(e);
    // clear conditional id values

    onClearIdFields();
    seedValues({
      idNumber: '',
      idState: '',
      idCountry: '',
      idMilitaryBranch: '',
    });
  };

  const { getDialogProps, getTriggerProps } = usePopoverDialog({
    placement: 'bottom-end',
  });

  useEffect(() => {
    onChangeComplete(isComplete);
  }, [isComplete, onChangeComplete]);

  useEffect(() => {
    if (!isComplete && submitAttempts > 0) {
      validate();
    }
  }, [submitAttempts, validate]);

  return (
    <div className={classNames('enrollment-form', className)} css={styles.form}>
      <div css={styles.twoColumnRow}>
        <div className='static-amount-field' css={styles.staticAmountField}>
          <Text size='small' color='subdued'>
            {t('Amount of initial sale/transaction')}
          </Text>
          <Text size='large'>{formattedBilledAmount}</Text>
        </div>
      </div>
      <Heading level={3}>{t('Applicant Information')}</Heading>
      <div css={styles.twoColumnRow}>
        <TextField {...getFieldProps('firstName')} label={FIELD_LABEL_MAP.firstName} onChange={onAnyFieldChange} />
        <TextField {...getFieldProps('middleName')} label={FIELD_LABEL_MAP.middleName} onChange={onAnyFieldChange} />
      </div>
      <div css={styles.twoColumnRow}>
        <TextField {...getFieldProps('lastName')} label={FIELD_LABEL_MAP.lastName} onChange={onAnyFieldChange} />
        <DropdownField {...getFieldProps('suffix')} label={FIELD_LABEL_MAP.suffix} onChange={onAnyFieldChange}>
          {SUFFIX_OPTIONS.map((option) => (
            <DropdownField.Option key={option.value} value={option.value}>
              {option.label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>

      <div css={styles.twoColumnRow}>
        <DateField {...getFieldProps('dateOfBirth')} label={FIELD_LABEL_MAP.dateOfBirth} onChange={onAnyFieldChange} />
      </div>

      <div css={styles.twoColumnRow}>
        <EmailField {...getFieldProps('email')} label={FIELD_LABEL_MAP.email} onChange={onAnyFieldChange} />
        <SsnField {...getFieldProps('ssn')} label={FIELD_LABEL_MAP.ssn} onChange={onAnyFieldChange} />
      </div>

      <div css={styles.twoColumnRow}>
        <PhoneField {...getFieldProps('phoneNumber')} label={FIELD_LABEL_MAP.phoneNumber} onChange={onAnyFieldChange} />
        <DropdownField
          {...getFieldProps('phoneNumberType')}
          label={FIELD_LABEL_MAP.phoneNumberType}
          onChange={onAnyFieldChange}
        >
          <DropdownField.Option value={''} key={'select-phone-type'}>
            {t('Select Phone Type')}
          </DropdownField.Option>
          {PHONE_TYPE_OPTIONS.map((option) => (
            <DropdownField.Option key={option.value} value={option.value}>
              {option.label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>

      <div css={styles.twoColumnRow}>
        <TextField {...getFieldProps('address')} label={FIELD_LABEL_MAP.address} onChange={onAnyFieldChange} />
        <TextField {...getFieldProps('address2')} label={FIELD_LABEL_MAP.address2} onChange={onAnyFieldChange} />
      </div>
      <div css={styles.twoColumnRow}>
        <TextField {...getFieldProps('city')} label={FIELD_LABEL_MAP.city} onChange={onAnyFieldChange} />
        <DropdownField {...getFieldProps('state')} label={FIELD_LABEL_MAP.state} onChange={onAnyFieldChange}>
          {stateOptions.US.map((option) => (
            <DropdownField.Option key={option.value} value={option.value}>
              {option.label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>
      <div css={styles.twoColumnRow}>
        <TextField {...getFieldProps('postalCode')} label={FIELD_LABEL_MAP.postalCode} onChange={onAnyFieldChange} />
      </div>
      <Text size='large' weight='bold'>
        {FIELD_LABEL_MAP.housingStatus}
      </Text>
      <RadioField {...getFieldProps('housingStatus')} onChange={onAnyFieldChange}>
        {HOUSING_STATUS_OPTIONS.map((option) => (
          <RadioField.Radio key={option.value} value={option.value}>
            {option.label}
          </RadioField.Radio>
        ))}
      </RadioField>

      <div css={styles.popoverColumn}>
        <MoneyField
          {...getFieldProps('monthlyNetIncome')}
          label={FIELD_LABEL_MAP.monthlyNetIncome}
          onChange={onAnyFieldChange}
        />

        <div>
          <NakedButton {...getTriggerProps()} size='large'>
            <Icon name='info' color='default' />
          </NakedButton>
          <PopoverDialog {...getDialogProps()} css={styles.dialog}>
            <Heading level={2} css={styles.dialogHeading}>
              {t('Net Income Disclosure')}
            </Heading>
            <Text>
              <Trans t={t}>
                Your “Net Income” includes what you earn or reasonably expect to earn from employment, investments,
                retirement, social security benefits and public assistance. You can also include money that someone else
                deposits regularly into your account (individual or joint) and the amount that you have available to
                spend from your assets. If you are 21 or over, you may also include the amount of someone else’s income
                that is regularly used to pay your expenses. Alimony, child support or separate maintenance income need
                not be included unless relied upon for credit.
                <br />
                <br />
                <b>WI Residents Only:</b> If this is an individual account, please also include your spouse’s income.
              </Trans>
            </Text>
          </PopoverDialog>
        </div>
      </div>
      <Heading level={3}>{t('Applicant ID Verification')}</Heading>

      <div css={styles.twoColumnRow}>
        <DropdownField {...getFieldProps('idType')} label={FIELD_LABEL_MAP.idType} onChange={onChangeIdType}>
          {ID_TYPE_OPTIONS.map((option) => (
            <DropdownField.Option key={option.value} value={option.value}>
              {option.label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>

      <div css={styles.twoColumnRow}>
        <TextField {...getFieldProps('idNumber')} label={FIELD_LABEL_MAP.idNumber} onChange={onAnyFieldChange} />
      </div>

      {showIdState && (
        <div css={styles.twoColumnRow}>
          <DropdownField {...getFieldProps('idState')} label={FIELD_LABEL_MAP.idState} onChange={onAnyFieldChange}>
            {stateOptions.US.map((option) => (
              <DropdownField.Option key={option.value} value={option.value}>
                {option.label}
              </DropdownField.Option>
            ))}
          </DropdownField>
        </div>
      )}
      {showMilitaryBranch && (
        <div css={styles.twoColumnRow}>
          <TextField
            {...getFieldProps('idMilitaryBranch')}
            label={FIELD_LABEL_MAP.idMilitaryBranch}
            onChange={onAnyFieldChange}
          />
        </div>
      )}

      <div css={styles.twoColumnRow}>
        <DateField
          {...getFieldProps('idExpirationDate')}
          label={FIELD_LABEL_MAP.idExpirationDate}
          onChange={onAnyFieldChange}
        />
      </div>

      <div css={styles.twoColumnRow}>
        {showIdCountry && (
          <TextField {...getFieldProps('idCountry')} label={FIELD_LABEL_MAP.idCountry} onChange={onAnyFieldChange} />
        )}
      </div>
      <div css={styles.agreeBox}>
        <CheckboxField
          {...getFieldProps('agreeToTerms')}
          onChange={onAnyFieldChange}
          label={t(`By continuing, I affirm the applicant has authorized Synchrony Bank to obtain information from consumer
        reporting agencies to determine if they are prequalified for credit. In addition, if they are prequalified, the
        applicant acknowledges that they will need to accept the credit offer and complete the application process. I
        affirm the applicant acknowledges that they are limited to 4 prequalification attempts per day; after their 4th
        attempt any future requests will not be accepted for the remainder of that day.`)}
        />
      </div>

      {errorState && (
        <BannerNotification
          status='error'
          message={
            <>
              {errorState.message && <Text css={styles.errorMessage}>{errorState.message}</Text>}
              {errorState.fields.length > 0 && (
                <div>
                  <Text weight='bold'>{t('Please review the following fields:')}</Text>
                  <ul>
                    {errorState.fields.map((fieldError, i) => (
                      <li key={`field-${i}-${fieldError.field}`}>
                        {FIELD_LABEL_MAP[fieldError.field] ?? fieldError.field}: {fieldError.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          }
        />
      )}
    </div>
  );
};
